import { useEffect, useMemo, useState } from 'react';
import {
  Button,
  Input,
  MessageBar,
  MessageBarBody,
  Spinner,
  Text,
} from '@fluentui/react-components';
import type {
  CreateIncidentResponse,
  Directory,
  Preferences,
  ServiceProviderOption,
} from '../../api/src/shared/contract';
import { createIncident, uploadAttachment } from '../api/client';
import { findByEmail } from '../lib/directory';
import { sanitizeHtml } from '../lib/sanitize';
import { prepareTitle, TITLE_MAX, truncateTitle } from '../lib/title';
import { rewriteInlineImages } from '../lib/inlineImages';
import {
  ATTACHMENT_CONCURRENCY,
  getAttachmentContent,
  listAttachments,
  mapLimited,
  type MailAttachment,
} from '../office/attachments';
import type { MessageSnapshot } from '../office/mailbox';
import { AssigneeField, GroupField, ProviderField } from './fields/AssignmentFields';
import {
  AttachmentList,
  defaultAttachmentSelection,
} from './fields/AttachmentList';
import {
  convertDescriptionToPlain,
  DescriptionEditor,
} from './fields/DescriptionEditor';
import { LabeledField } from './fields/LabeledField';
import { CcList, RequesterField } from './fields/RequesterAndCc';
import { Header } from './Header';
import { usePaneStyles } from './styles';

/** Ticket facts the Teams host turns into the chat card. */
export interface CreatedTicket {
  incidentNumber: string;
  incidentUrl: string;
  title: string;
  groupName: string;
  assigneeName: string;
  requesterName: string;
}

interface TicketFormProps {
  message: MessageSnapshot;
  directory: Directory;
  preferences: Preferences;
  providers: ServiceProviderOption[];
  providerId: string;
  /** Reload session (and directory) for a different ESM provider. */
  onProviderChange: (providerId: string) => void;
  /** Outlook opens Settings; Teams omits CC/attachments and has no settings gear. */
  host?: 'outlook' | 'teams';
  onSettings?: () => void;
  /**
   * Teams closes the dialog and posts a chat card once the ticket exists. When set, the
   * in-pane success view is skipped because the dialog is already gone.
   */
  onCreated?: (ticket: CreatedTicket) => void;
}

type SubmitState =
  | { phase: 'idle' }
  | { phase: 'creating' }
  | { phase: 'uploading'; created: CreateIncidentResponse; done: number; total: number }
  | {
      phase: 'done';
      created: CreateIncidentResponse;
      failures: { name: string; error: string }[];
    }
  | { phase: 'error'; error: string };

export function TicketForm({
  message,
  directory,
  preferences,
  providers,
  providerId,
  onProviderChange,
  host = 'outlook',
  onSettings,
  onCreated,
}: TicketFormProps) {
  const styles = usePaneStyles();
  const teamsHost = host === 'teams';
  const attachments = useMemo(
    () => (teamsHost ? [] : listAttachments()),
    [message, teamsHost],
  );

  const [groupId, setGroupId] = useState<number | null>(preferences.defaultGroupId);
  const [assigneeId, setAssigneeId] = useState<number | null>(preferences.defaultAssigneeId);
  const [clearedName, setClearedName] = useState<string | null>(null);
  const [requesterEmail, setRequesterEmail] = useState(message.sender?.email ?? '');
  const [ccSelected, setCcSelected] = useState<Set<string>>(() => new Set());
  const [title, setTitle] = useState(() => prepareTitle(message.subject));
  const [descriptionHtml, setDescriptionHtml] = useState(() => sanitizeHtml(message.bodyHtml));
  const [originalHtml] = useState(() => sanitizeHtml(message.bodyHtml));
  const [plainMode, setPlainMode] = useState(false);
  const [attSelected, setAttSelected] = useState(() =>
    defaultAttachmentSelection(attachments, preferences),
  );
  const [submit, setSubmit] = useState<SubmitState>({ phase: 'idle' });
  const [providerBusy, setProviderBusy] = useState(false);

  // When the parent reloads session for a new provider, adopt that provider's defaults.
  useEffect(() => {
    setGroupId(preferences.defaultGroupId);
    setAssigneeId(preferences.defaultAssigneeId);
    setClearedName(null);
    setProviderBusy(false);
  }, [providerId, preferences.defaultGroupId, preferences.defaultAssigneeId]);

  const requester = findByEmail(directory.people, requesterEmail);
  const canCreate =
    Boolean(requester) &&
    groupId != null &&
    title.trim().length > 0 &&
    !providerBusy &&
    (submit.phase === 'idle' || submit.phase === 'error' || submit.phase === 'done');

  // Teams hands the ticket back to the host once it exists; the host closes the dialog and
  // posts the chat card. Outlook leaves onCreated unset and shows the in-pane success view.
  useEffect(() => {
    if (submit.phase !== 'done' || !onCreated) return;
    onCreated({
      incidentNumber: submit.created.number,
      incidentUrl: submit.created.url ?? '',
      title: title.trim(),
      groupName: directory.groups.find((g) => g.id === groupId)?.name ?? '',
      assigneeName: directory.people.find((p) => p.id === assigneeId)?.name ?? '',
      requesterName: requester?.name ?? '',
    });
  }, [submit, onCreated, directory, groupId, assigneeId, title, requester]);

  function handleGroupChange(next: number | null): void {
    setGroupId(next);
    if (assigneeId == null || next == null) return;
    const stillIn = directory.people.find(
      (p) => p.id === assigneeId && p.agent && p.groupIds?.includes(next),
    );
    if (!stillIn) {
      const previous = directory.people.find((p) => p.id === assigneeId);
      setClearedName(previous?.name ?? 'Previous assignee');
      setAssigneeId(null);
    } else {
      setClearedName(null);
    }
  }

  function resetForm(): void {
    setGroupId(preferences.defaultGroupId);
    setAssigneeId(preferences.defaultAssigneeId);
    setClearedName(null);
    setRequesterEmail(message.sender?.email ?? '');
    setCcSelected(new Set());
    setTitle(prepareTitle(message.subject));
    setDescriptionHtml(sanitizeHtml(message.bodyHtml));
    setPlainMode(false);
    setAttSelected(defaultAttachmentSelection(attachments, preferences));
    setSubmit({ phase: 'idle' });
  }

  async function handleCreate(): Promise<void> {
    if (!requester || groupId == null) return;

    setSubmit({ phase: 'creating' });
    try {
      const ccEmails = [...ccSelected];
      const description = rewriteInlineImages(
        sanitizeHtml(descriptionHtml),
        attachments.map((a) => ({
          ...(a.contentId ? { contentId: a.contentId } : {}),
          name: a.name,
          include: attSelected.has(a.id),
        })),
      );
      const body = {
        providerId,
        title: truncateTitle(title),
        description,
        requesterEmail: requester.email,
        groupId,
        ...(assigneeId != null ? { assigneeId } : {}),
        ...(ccEmails.length > 0 ? { ccEmails } : {}),
      };

      const created = await createIncident(body);
      const toUpload = attachments.filter((a) => attSelected.has(a.id));

      if (toUpload.length === 0) {
        setSubmit({ phase: 'done', created, failures: [] });
        return;
      }

      setSubmit({ phase: 'uploading', created, done: 0, total: toUpload.length });
      const failures: { name: string; error: string }[] = [];
      let done = 0;

      await mapLimited(toUpload, ATTACHMENT_CONCURRENCY, async (att: MailAttachment) => {
        try {
          const content = await getAttachmentContent(att.id);
          await uploadAttachment(created.id, providerId, {
            name: content.name,
            contentType: content.contentType,
            bytes: content.bytes,
          });
        } catch (error) {
          failures.push({
            name: att.name,
            error: error instanceof Error ? error.message : String(error),
          });
        } finally {
          done += 1;
          setSubmit({ phase: 'uploading', created, done, total: toUpload.length });
        }
      });

      setSubmit({ phase: 'done', created, failures });
    } catch (error) {
      setSubmit({
        phase: 'error',
        error: error instanceof Error ? error.message : String(error),
      });
    }
  }

  if (submit.phase === 'done') {
    const groupName = directory.groups.find((g) => g.id === groupId)?.name;
    const assigneeName = directory.people.find((p) => p.id === assigneeId)?.name;
    return (
      <ResultView
        created={submit.created}
        failures={teamsHost ? [] : submit.failures}
        {...(groupName ? { groupName } : {})}
        {...(assigneeName ? { assigneeName } : {})}
        {...(onSettings ? { onSettings } : {})}
        teamsHost={teamsHost}
      />
    );
  }

  const busy = submit.phase === 'creating' || submit.phase === 'uploading';

  return (
    <div className={styles.root}>
      <Header title="Create ticket" {...(onSettings ? { onSettings } : {})} />
      <div className={styles.body}>
        {teamsHost ? (
          <MessageBar intent="info">
            <MessageBarBody>
              Set your default service provider and other preferences in the Outlook add-in.
            </MessageBarBody>
          </MessageBar>
        ) : null}
        {submit.phase === 'error' ? (
          <MessageBar intent="error">
            <MessageBarBody>{submit.error}</MessageBarBody>
          </MessageBar>
        ) : null}
        {submit.phase === 'uploading' ? (
          <MessageBar intent="info">
            <MessageBarBody>
              Uploading attachments… {submit.done} of {submit.total}
            </MessageBarBody>
          </MessageBar>
        ) : null}

        <ProviderField
          providers={providers}
          value={providerId}
          disabled={busy || providerBusy}
          onChange={(next) => {
            if (next === providerId) return;
            setProviderBusy(true);
            onProviderChange(next);
          }}
        />

        <GroupField groups={directory.groups} value={groupId} onChange={handleGroupChange} />

        <AssigneeField
          people={directory.people}
          groupId={groupId}
          value={assigneeId}
          clearedName={clearedName}
          onChange={setAssigneeId}
          onDismissCleared={() => setClearedName(null)}
        />

        <RequesterField
          people={directory.people}
          value={requesterEmail}
          onChange={setRequesterEmail}
        />

        {teamsHost ? null : (
          <CcList
            recipients={message.cc}
            people={directory.people}
            selected={ccSelected}
            onToggle={(email) => {
              setCcSelected((prev) => {
                const next = new Set(prev);
                if (next.has(email)) next.delete(email);
                else next.add(email);
                return next;
              });
            }}
          />
        )}

        <LabeledField label="Title" required meta={`${title.length} / ${TITLE_MAX}`}>
          <Input
            value={title}
            maxLength={TITLE_MAX}
            onChange={(_, d) => setTitle(d.value)}
          />
        </LabeledField>

        <DescriptionEditor
          html={descriptionHtml}
          plainMode={plainMode}
          onChangeHtml={setDescriptionHtml}
          onTogglePlain={() => {
            if (!plainMode) {
              setDescriptionHtml(convertDescriptionToPlain(descriptionHtml));
              setPlainMode(true);
            } else {
              setPlainMode(false);
            }
          }}
          onRevertHtml={() => {
            setDescriptionHtml(originalHtml);
            setPlainMode(false);
          }}
          canRevert={plainMode && descriptionHtml !== originalHtml}
        />

        {teamsHost ? null : (
          <AttachmentList
            attachments={attachments}
            preferences={preferences}
            selected={attSelected}
            onToggle={(id) => {
              setAttSelected((prev) => {
                const next = new Set(prev);
                if (next.has(id)) next.delete(id);
                else next.add(id);
                return next;
              });
            }}
          />
        )}

        <div className={styles.footer}>
          <Button
            appearance="primary"
            style={{ flex: 1 }}
            disabled={!canCreate || busy}
            onClick={() => void handleCreate()}
          >
            {busy ? (
              <Spinner size="tiny" label={submit.phase === 'creating' ? 'Creating…' : 'Uploading…'} />
            ) : (
              'Create ticket'
            )}
          </Button>
          {!teamsHost ? (
            <Button appearance="secondary" disabled={busy} onClick={resetForm}>
              Reset
            </Button>
          ) : null}
        </div>
        {!requester ? (
          <Text className={styles.hintWarn}>
            Create ticket stays disabled until the requester resolves to a SolarWinds user.
          </Text>
        ) : null}
      </div>
    </div>
  );
}

function ResultView({
  created,
  failures,
  groupName,
  assigneeName,
  onSettings,
  teamsHost,
}: {
  created: CreateIncidentResponse;
  failures: { name: string; error: string }[];
  groupName?: string;
  assigneeName?: string;
  onSettings?: () => void;
  teamsHost?: boolean;
}) {
  const styles = usePaneStyles();
  const assignment = [
    assigneeName ? `Assigned to ${assigneeName}` : 'Unassigned',
    groupName ? `in ${groupName}` : null,
  ]
    .filter(Boolean)
    .join(' ');

  return (
    <div className={styles.root}>
      <Header title="Create ticket" {...(onSettings ? { onSettings } : {})} />
      <div className={styles.body}>
        <MessageBar intent="success">
          <MessageBarBody>
            <strong>Incident #{created.number} created.</strong>
            <br />
            {assignment}.
          </MessageBarBody>
        </MessageBar>

        {failures.length > 0 ? (
          <MessageBar intent="warning">
            <MessageBarBody>
              <strong>
                {failures.length} attachment{failures.length === 1 ? '' : 's'} failed.
              </strong>
              <br />
              {failures.map((f) => f.name).join(', ')} did not upload. The ticket was still
              created.
            </MessageBarBody>
          </MessageBar>
        ) : null}

        {created.url ? (
          <Button
            appearance="secondary"
            as="a"
            href={created.url}
            target="_blank"
            rel="noreferrer"
            style={{ width: '100%' }}
          >
            Open ticket in SolarWinds
          </Button>
        ) : (
          <Text className={styles.hint}>
            Ticket #{created.number} was created. SolarWinds did not return a portal link.
          </Text>
        )}

        {teamsHost ? (
          <Text className={styles.hint} style={{ textAlign: 'center' }}>
            Set your default service provider in the Outlook add-in Settings.
          </Text>
        ) : null}
      </div>
    </div>
  );
}
